import React, { useState, useRef } from 'react';
import { taxEngineAPI } from '../../services/api';
import { saveBlob, money } from '../../pages/tax/taxWorkspace';
import './ExcelHouseholdTaxImportWorkspace.css';

export default function ExcelHouseholdTaxImportWorkspace({
  tenantSlug,
  onBack,
  onImportSuccess,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [validationData, setValidationData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [filterTab, setFilterTab] = useState('ALL'); // 'ALL' | 'VALID' | 'ERRORS'
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      setErrorMessage('');
      const response = await taxEngineAPI.downloadImportTemplate(tenantSlug);
      saveBlob(response.data, 'Households_Tax_Import_Template.xlsx');
    } catch (err) {
      console.error('Failed to download template:', err);
      setErrorMessage(
        err.response?.data?.error || 'टेम्पलेट डाऊनलोड करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.'
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setErrorMessage('कृपया फक्त वैध एक्सेल फाईल (.xlsx किंवा .xls) निवडा.');
      return;
    }
    setSelectedFile(file);
    setValidationData(null);
    setImportResult(null);
    setErrorMessage('');
    // Automatically trigger validation preview
    runValidation(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const runValidation = async (fileToValidate) => {
    const file = fileToValidate || selectedFile;
    if (!file) {
      setErrorMessage('कृपया तपासणीसाठी प्रथम एक्सेल फाईल निवडा.');
      return;
    }
    try {
      setValidating(true);
      setErrorMessage('');
      const formData = new FormData();
      formData.append('file', file);
      const res = await taxEngineAPI.importHouseholdTaxExcel(tenantSlug, formData, true);
      setValidationData(res.data);
    } catch (err) {
      console.error('Validation failed:', err);
      setErrorMessage(
        err.response?.data?.error || 'एक्सेल डेटा तपासताना त्रुटी आली. कृपया फाईलचे स्वरूप तपासा.'
      );
    } finally {
      setValidating(false);
    }
  };

  const handleFinalImport = async () => {
    if (!selectedFile) return;
    try {
      setImporting(true);
      setErrorMessage('');
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await taxEngineAPI.importHouseholdTaxExcel(tenantSlug, formData, false);
      setImportResult(res.data);
      if (onImportSuccess) {
        onImportSuccess();
      }
    } catch (err) {
      console.error('Final import failed:', err);
      setErrorMessage(
        err.response?.data?.error || 'अंतिम डेटा आयात करताना त्रुटी आली. कृपया पुन्हा प्रयत्न करा.'
      );
    } finally {
      setImporting(false);
    }
  };

  const resetAll = () => {
    setSelectedFile(null);
    setValidationData(null);
    setImportResult(null);
    setErrorMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredRows = (validationData?.rows || []).filter((r) => {
    if (filterTab === 'VALID') return r.isValid;
    if (filterTab === 'ERRORS') return !r.isValid;
    return true;
  });

  return (
    <div className="gp-import-workspace">
      {/* ── Top Header / Breadcrumb Bar ── */}
      <div className="gp-import-header-card">
        <div className="gp-import-title-group">
          <div className="gp-import-icon-badge">📊</div>
          <div>
            <h2>घरे, नागरिक, मालमत्ता व कर मागणी एकत्रित आयात (Excel Import)</h2>
            <p>अधिकृत एक्सेल (.xlsx) शीटद्वारे कुटुंबांची नोंद, मालमत्ता व चालू/थकीत करांची थेट निर्मिती</p>
          </div>
        </div>

        <div className="gp-import-header-actions">
          <button
            type="button"
            className="gp-import-download-template-btn"
            onClick={handleDownloadTemplate}
            disabled={downloading}
          >
            📥 {downloading ? 'डाऊनलोड होत आहे…' : 'नमुना टेम्पलेट (.xlsx)'}
          </button>
          <button
            type="button"
            className="gp-import-back-btn"
            onClick={onBack}
            disabled={importing}
          >
            ← कुटुंब यादीकडे परत जा
          </button>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {errorMessage && (
        <div className="gp-import-error-alert">
          <span>⚠️</span>
          <span>{errorMessage}</span>
        </div>
      )}

      {/* ── 3-Step Process Flow ── */}
      <div className="gp-import-steps-grid">
        <div className="gp-import-step-box">
          <div className="gp-import-step-box-header">
            <span className="gp-step-pill">१</span>
            <h3>नमुना एक्सेल डाऊनलोड</h3>
          </div>
          <p>मराठी कॉलम्स, बांधकाम व वापराची ड्रॉपडाऊन यादी आणि स्वयं-गणित सूत्रे असलेले टेम्पलेट मिळवा.</p>
        </div>

        <div className={`gp-import-step-box ${selectedFile ? 'active' : ''}`}>
          <div className="gp-import-step-box-header">
            <span className="gp-step-pill">२</span>
            <h3>माहिती भरा व फाईल जोडा</h3>
          </div>
          <p>घर क्रमांक, करदात्याचे नाव, मोबाईल, क्षेत्रफळ, बांधकाम प्रकार व करांच्या रकमा भरा.</p>
        </div>

        <div className={`gp-import-step-box ${validationData ? (validationData.errorCount === 0 ? 'completed' : 'active') : ''}`}>
          <div className="gp-import-step-box-header">
            <span className="gp-step-pill">३</span>
            <h3>ड्राय-रन तपासणी व अंतिम आयात</h3>
          </div>
          <p>
            {validating
              ? 'डेटा तपासला जात आहे…'
              : validationData
                ? (validationData.errorCount === 0
                  ? `✓ सर्व ${validationData.validCount} नोंदी वैध आहेत.`
                  : `⚠️ ${validationData.errorCount} त्रुटी सापडल्या आहेत.`)
                : 'डेटाबेसमध्ये जतन करण्यापूर्वी संपूर्ण डेटाचे परीक्षण व पडताळणी.'}
          </p>
        </div>
      </div>

      {/* ── File Upload / Dropzone (Shown when no active file or when changing file) ── */}
      {!importResult && (
        <div
          className={`gp-import-dropzone-card ${isDragging ? 'drag-active' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx, .xls"
            style={{ display: 'none' }}
            onChange={(e) => e.target.files && handleFileSelect(e.target.files[0])}
          />
          <div className="gp-dropzone-illustration">📁</div>
          <h3 className="gp-dropzone-title">
            {selectedFile ? 'दुसरी फाईल निवडण्यासाठी येथे क्लिक करा' : 'भरलेली एक्सेल (.xlsx) फाईल येथे ड्रॅग करा किंवा निवडा'}
          </h3>
          <p className="gp-dropzone-subtitle">कमाल मर्यादा: 15 MB | फक्त .xlsx किंवा .xls फाईल्स</p>

          {selectedFile && (
            <div className="gp-selected-file-preview" onClick={(e) => e.stopPropagation()}>
              <span>📄 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</span>
              <button
                type="button"
                className="gp-file-remove-button"
                onClick={resetAll}
                title="काढून टाका"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Final Success Celebration Card ── */}
      {importResult && (
        <div className="gp-success-card">
          <div className="gp-success-header">
            <div className="gp-success-party-icon">🎉</div>
            <div>
              <h3>{importResult.message || 'नोंदी यशस्वीरित्या आयात झाल्या आहेत!'}</h3>
              <p>सर्व कुटुंबे, मालमत्ता, नागरिक व कर मागण्या प्रणालीमध्ये जोडल्या गेल्या आहेत.</p>
            </div>
          </div>

          {importResult.summary && (
            <div className="gp-success-metrics-grid">
              <div className="gp-metric-tile">
                <span>एकूण प्रक्रिया केलेल्या नोंदी</span>
                <strong>{importResult.summary.totalRowsProcessed}</strong>
              </div>
              <div className="gp-metric-tile">
                <span>तयार झालेली कुटुंबे (Households)</span>
                <strong>{importResult.summary.householdsCreated}</strong>
              </div>
              <div className="gp-metric-tile">
                <span>तयार झालेल्या मालमत्ता (Properties)</span>
                <strong>{importResult.summary.propertiesCreated}</strong>
              </div>
              <div className="gp-metric-tile">
                <span>तयार झालेली कर खाती (Tax Accounts)</span>
                <strong>{importResult.summary.taxAccountsCreated}</strong>
              </div>
              <div className="gp-metric-tile">
                <span>निर्मित कर मागण्या (Demands)</span>
                <strong>{importResult.summary.demandsCreated}</strong>
              </div>
              <div className="gp-metric-tile highlight">
                <span>एकूण कर आकारणी रक्कम</span>
                <strong>{money(importResult.summary.totalAssessedAmount)}</strong>
              </div>
            </div>
          )}

          <div className="gp-success-actions">
            <button
              type="button"
              className="gp-btn-primary-import"
              onClick={onBack}
            >
              🏠 कुटुंब व्यवस्थापनाकडे जा (Go to Households)
            </button>
            <button
              type="button"
              className="gp-btn-outline"
              onClick={resetAll}
            >
              📊 आणखी एक फाईल आयात करा
            </button>
          </div>
        </div>
      )}

      {/* ── Full Width Preview & Validation Table ── */}
      {validationData && !importResult && (
        <div className="gp-preview-card">
          <div className="gp-preview-card-header">
            <div className="gp-stat-chips-group">
              <span className="gp-stat-chip total">एकूण पंक्ती: {validationData.totalRows}</span>
              <span className="gp-stat-chip valid">✓ वैध नोंदी: {validationData.validCount}</span>
              {validationData.errorCount > 0 && (
                <span className="gp-stat-chip invalid">⚠️ त्रुटी: {validationData.errorCount}</span>
              )}
            </div>

            <div className="gp-filter-tabs-group">
              <button
                type="button"
                className={`gp-filter-tab ${filterTab === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilterTab('ALL')}
              >
                सर्व ({validationData.totalRows})
              </button>
              <button
                type="button"
                className={`gp-filter-tab ${filterTab === 'VALID' ? 'active' : ''}`}
                onClick={() => setFilterTab('VALID')}
              >
                वैध ({validationData.validCount})
              </button>
              <button
                type="button"
                className={`gp-filter-tab ${filterTab === 'ERRORS' ? 'active' : ''}`}
                onClick={() => setFilterTab('ERRORS')}
              >
                त्रुटी असलेल्या ({validationData.errorCount})
              </button>
            </div>
          </div>

          <div className="gp-preview-table-wrapper">
            <table className="gp-preview-data-table">
              <thead>
                <tr>
                  <th>पंक्ती</th>
                  <th>स्थिती</th>
                  <th>घर क्र.</th>
                  <th>करदात्याचे नाव</th>
                  <th>मोबाईल</th>
                  <th>प्रभाग</th>
                  <th>पत्ता</th>
                  <th>बांधकाम</th>
                  <th>वापर</th>
                  <th>क्षेत्रफळ (चौ.फूट)</th>
                  <th>घरपट्टी (चालू + थकीत)</th>
                  <th>दिवाबत्ती (चालू + थकीत)</th>
                  <th>पाणीपट्टी (चालू + थकीत)</th>
                  <th>आरोग्य (चालू + थकीत)</th>
                  <th>एकूण कर (₹)</th>
                  <th>तपासणी शेरा / त्रुटी तपशील</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr key={row.rowNumber} className={row.isValid ? 'row-valid' : 'row-invalid'}>
                    <td>#{row.rowNumber}</td>
                    <td>
                      <span className={`gp-status-pill ${row.isValid ? 'valid' : 'invalid'}`}>
                        {row.isValid ? '✓ वैध' : '⚠️ त्रुटी'}
                      </span>
                    </td>
                    <td>
                      <span className="gp-ghar-badge">{row.ghar_kramank || '-'}</span>
                    </td>
                    <td>
                      <strong>{row.taxpayer_name || '-'}</strong>
                    </td>
                    <td>{row.mobile_number || row.raw_mobile || '-'}</td>
                    <td>{row.ward_number || '-'}</td>
                    <td>{row.property_address || 'गावठाण'}</td>
                    <td>{row.raw_construction || row.construction_type || '-'}</td>
                    <td>{row.raw_usage || row.usage_type || '-'}</td>
                    <td>{row.built_up_area_sqft || row.carpet_area_sqft || 0} चौ.फूट</td>
                    <td>
                      {money((row.gharpatti_current || 0) + (row.gharpatti_pending || 0))}
                    </td>
                    <td>
                      {money((row.vijkar_current || 0) + (row.vijkar_pending || 0))}
                    </td>
                    <td>
                      {money((row.panipatti_current || 0) + (row.panipatti_pending || 0))}
                    </td>
                    <td>
                      {money((row.arogya_kar_current || 0) + (row.arogya_kar_pending || 0))}
                    </td>
                    <td>
                      <span className="gp-tax-amount">{money(row.total_all_taxes || 0)}</span>
                    </td>
                    <td>
                      {row.isValid ? (
                        <span className="gp-ok-note-cell">✓ आयात करण्यासाठी सज्ज</span>
                      ) : (
                        <div className="gp-error-note-cell">
                          {(row.errors || []).map((err, i) => (
                            <div key={i}>• {err}</div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="gp-preview-card-footer">
            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
              {validationData.validCount > 0 ? (
                <span>
                  <strong>{validationData.validCount}</strong> वैध नोंदी आयात करण्यासाठी उपलब्ध आहेत.
                  {validationData.errorCount > 0 && (
                    <span style={{ color: '#b91c1c', marginLeft: '0.5rem' }}>
                      ({validationData.errorCount} त्रुटी असलेल्या नोंदी वगळल्या जातील)
                    </span>
                  )}
                </span>
              ) : (
                <span style={{ color: '#b91c1c' }}>
                  फायलीमध्ये एकही वैध नोंद सापडली नाही. कृपया त्रुटी तपासून एक्सेल दुरुस्त करा.
                </span>
              )}
            </div>

            <div className="gp-action-buttons-group">
              <button
                type="button"
                className="gp-btn-outline"
                onClick={resetAll}
                disabled={importing}
              >
                🔄 दुसरी फाईल निवडा
              </button>
              <button
                type="button"
                className="gp-btn-outline"
                onClick={onBack}
                disabled={importing}
              >
                रद्द करा
              </button>
              <button
                type="button"
                className="gp-btn-primary-import"
                onClick={handleFinalImport}
                disabled={importing || validationData.validCount === 0}
              >
                {importing ? (
                  <>डेटाबेसमध्ये जतन होत आहे…</>
                ) : (
                  <>✓ {validationData.validCount} नोंदी अंतिम आयात करा (Confirm & Import)</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
